import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import api, { usageAPI, subscriptionAPI } from '../lib/api';
import {
  User,
  Mail,
  CreditCard,
  Activity,
  Shield,
  Bell,
  Zap,
  Beaker,
  Save,
  RotateCcw,
  Check,
  Lock,
  Palette,
  Upload,
  Trash2,
  ImageIcon,
} from 'lucide-react';

const DEFAULT_PRICES = { PLA: 20, ABS: 22, PETG: 25, TPU: 35, PVA: 60, HIPS: 25 };

const Account = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [upgrading, setUpgrading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Filament state
  const [filamentPrices, setFilamentPrices] = useState(DEFAULT_PRICES);
  const [filamentSaving, setFilamentSaving] = useState(false);
  const [filamentSaved, setFilamentSaved] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState('');
  const [pwError, setPwError] = useState('');
  const [isCustomPrices, setIsCustomPrices] = useState(false);

  // Branding state
  const [businessName, setBusinessName] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    loadUsageData();
    loadFilamentPrices();
    loadBranding();
  }, []);

  // Poll payment status when returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await subscriptionAPI.getCheckoutStatus(sessionId);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        await refreshUser();
        setSearchParams({});
        loadUsageData();
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        setSearchParams({});
        return;
      }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error('Status poll error:', e);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setPaymentStatus('error');
      }
    }
  }, [setSearchParams, refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const upgrade = searchParams.get('upgrade');
    if (sessionId && upgrade === 'success') {
      setPaymentStatus('processing');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  // Support deep-linking to a specific tab via ?tab=<id>. Quota CTAs
  // send users straight to ?tab=subscription so they land on the plan
  // options page instead of having to hunt for the tab manually.
  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    const valid = ['profile', 'branding', 'security', 'filament', 'subscription', 'usage'];
    if (requestedTab && valid.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  const loadUsageData = async () => {
    try {
      const response = await usageAPI.getCurrent();
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to load usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilamentPrices = async () => {
    try {
      const res = await api.get('/user/preferences/filament-prices');
      setFilamentPrices(res.data.prices);
      setIsCustomPrices(res.data.is_custom);
    } catch (e) { /* use defaults */ }
  };

  const saveFilamentPrices = async () => {
    setFilamentSaving(true);
    try {
      await api.put('/user/preferences/filament-prices', filamentPrices);
      setIsCustomPrices(true);
      setFilamentSaved(true);
      setTimeout(() => setFilamentSaved(false), 2000);
    } catch (e) {
      console.error('Failed to save filament prices:', e);
    } finally {
      setFilamentSaving(false);
    }
  };

  const resetFilamentPrices = () => {
    setFilamentPrices({ ...DEFAULT_PRICES });
  };

  // Branding functions
  const loadBranding = async () => {
    try {
      const res = await api.get('/user/branding');
      setBusinessName(res.data.business_name || '');
      setHasLogo(res.data.has_logo);
      if (res.data.has_logo) {
        try {
          const logoRes = await api.get('/user/branding/logo', { responseType: 'blob' });
          setLogoPreview(URL.createObjectURL(logoRes.data));
        } catch (e) { /* logo fetch failed */ }
      }
    } catch (e) { /* defaults */ }
  };

  const saveBusinessName = async () => {
    setBrandingSaving(true);
    try {
      await api.put('/user/branding/business-name', { business_name: businessName });
      setBrandingSaved(true);
      setTimeout(() => setBrandingSaved(false), 2000);
    } catch (e) {
      console.error('Failed to save business name:', e);
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be under 2MB');
      return;
    }
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/user/branding/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoPreview(URL.createObjectURL(file));
      setHasLogo(true);
    } catch (e) {
      console.error('Logo upload failed:', e);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await api.delete('/user/branding/logo');
      setLogoPreview(null);
      setHasLogo(false);
    } catch (e) {
      console.error('Logo delete failed:', e);
    }
  };

  const planFeatures = {
    free: ['5 STL analyses/month', '3 image analyses', '10 AI chat messages', 'Community support'],
    pro: [
      '50 STL analyses/month',
      '30 image analyses',
      '500 AI chat messages',
      '25 Smart Supports',
      'Priority support',
      'Custom profiles',
    ],
    studio: [
      '200 STL analyses/month',
      '100 image analyses',
      '2000 AI chat messages',
      '100 Smart Supports',
      'Team access (3 seats)',
      'Exportable reports',
      'Enhanced AI engine',
    ],
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'branding', name: 'Branding', icon: Palette },
    { id: 'security', name: 'Security', icon: Lock },
    { id: 'filament', name: 'Filament Profiles', icon: Beaker },
    { id: 'subscription', name: 'Subscription', icon: CreditCard },
    { id: 'usage', name: 'Usage Stats', icon: Activity },
  ];

  const getUsagePercentage = (used, limit) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const handleUpgrade = async (plan) => {
    setUpgrading(true);
    try {
      const response = await subscriptionAPI.createCheckout(plan);
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
      alert('Failed to start checkout. Please try again.');
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    setUpgrading(true);
    try {
      const response = await subscriptionAPI.createPortal();
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Failed to open portal:', err);
      alert('Failed to open subscription management. Please try again.');
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="account-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
        <p className="text-text-secondary">
          Manage your profile, subscription, and preferences
        </p>
      </div>

      {/* Payment status notification */}
      {paymentStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-500/15 border border-green-500/30 rounded-xl flex items-center gap-3" data-testid="payment-success-banner">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-400">Payment Successful!</p>
            <p className="text-xs text-green-400/70">Your plan has been upgraded. Refreshing your account...</p>
          </div>
        </div>
      )}
      {paymentStatus === 'processing' && (
        <div className="mb-6 p-4 bg-primary/15 border border-primary/30 rounded-xl flex items-center gap-3" data-testid="payment-processing-banner">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-primary">Processing Payment...</p>
            <p className="text-xs text-primary/70">Please wait while we confirm your payment.</p>
          </div>
        </div>
      )}
      {paymentStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-500/15 border border-red-500/30 rounded-xl flex items-center gap-3" data-testid="payment-error-banner">
          <Zap className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-400">Payment verification failed</p>
            <p className="text-xs text-red-400/70">Please check your email for confirmation or contact support.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8 border-b border-text-muted/20">
        <div className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 flex items-center space-x-2 border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-primary text-white'
                    : 'border-transparent text-text-secondary hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
              <User className="w-6 h-6 text-primary" />
              <span>Profile Information</span>
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={user?.full_name || ''}
                  disabled
                  className="w-full px-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Email Address
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="flex-1 px-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white focus:outline-none focus:border-primary"
                  />
                  {user?.is_verified ? (
                    <span className="px-3 py-1 bg-success/20 text-success text-sm font-semibold rounded-lg flex items-center space-x-1">
                      <Shield className="w-4 h-4" />
                      <span>Verified</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-warning/20 text-warning text-sm font-semibold rounded-lg">
                      Not Verified
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Account Created
                </label>
                <input
                  type="text"
                  value={new Date(user?.created_at).toLocaleDateString()}
                  disabled
                  className="w-full px-4 py-3 bg-bg-dark border border-text-muted/20 rounded-lg text-white"
                />
              </div>
            </div>
          </div>

          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <Bell className="w-6 h-6 text-accent" />
              <span>Notifications</span>
            </h2>
            <div className="space-y-3">
              <label className="flex items-center justify-between">
                <span className="text-white">Email notifications for analysis completion</span>
                <input type="checkbox" className="w-5 h-5 rounded" defaultChecked />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-white">Weekly usage reports</span>
                <input type="checkbox" className="w-5 h-5 rounded" defaultChecked />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-white">Points and rewards updates</span>
                <input type="checkbox" className="w-5 h-5 rounded" defaultChecked />
              </label>
            </div>
          </div>
        </div>
      )}



      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          {user?.subscription_plan === 'free' ? (
            <div className="bg-bg-light p-8 rounded-xl border border-text-muted/20 text-center space-y-4">
              <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Pro Feature</h2>
              <p className="text-text-secondary text-sm max-w-md mx-auto">
                Upload your business logo and name to create branded cost estimation PDFs for your clients. Available on Pro and Studio plans.
              </p>
              <button
                onClick={() => setActiveTab('subscription')}
                className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition"
                data-testid="branding-upgrade-btn"
              >
                Upgrade to Pro
              </button>
            </div>
          ) : (
            <>
              {/* Business Logo */}
              <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20" data-testid="branding-logo-section">
                <h2 className="text-xl font-bold text-text-primary mb-1 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-primary" />
                  Business Logo
                </h2>
                <p className="text-sm text-text-secondary mb-5">
                  Your logo appears on exported quote PDFs next to the "Powered by PrintMind AI" watermark.
                </p>

                <div className="flex items-start gap-6">
                  {/* Logo Preview */}
                  <div className="w-40 h-28 rounded-lg border-2 border-dashed border-text-muted/30 flex items-center justify-center bg-bg-dark/50 overflow-hidden flex-shrink-0">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Business logo"
                        className="max-w-full max-h-full object-contain"
                        data-testid="logo-preview-img"
                      />
                    ) : (
                      <div className="text-center text-text-muted">
                        <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-40" />
                        <p className="text-xs">No logo</p>
                      </div>
                    )}
                  </div>

                  {/* Upload/Delete controls */}
                  <div className="space-y-3 flex-1">
                    <label
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm cursor-pointer transition ${
                        logoUploading
                          ? 'bg-primary/30 text-primary/60'
                          : 'bg-primary/15 text-primary hover:bg-primary/25'
                      }`}
                      data-testid="logo-upload-btn"
                    >
                      <Upload className="w-4 h-4" />
                      {logoUploading ? 'Uploading...' : hasLogo ? 'Replace Logo' : 'Upload Logo'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={logoUploading}
                        data-testid="logo-file-input"
                      />
                    </label>

                    {hasLogo && (
                      <button
                        onClick={handleLogoDelete}
                        className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition"
                        data-testid="logo-delete-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove logo
                      </button>
                    )}
                    <p className="text-xs text-text-muted">PNG or JPG, max 2MB. Recommended: 400×150px</p>
                  </div>
                </div>
              </div>

              {/* Business Name */}
              <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20" data-testid="branding-name-section">
                <h2 className="text-xl font-bold text-text-primary mb-1 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-accent" />
                  Business Name
                </h2>
                <p className="text-sm text-text-secondary mb-5">
                  Shown alongside your logo in the PDF header and footer.
                </p>

                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. 3D Pro Prints"
                    maxLength={100}
                    className="flex-1 bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                    data-testid="business-name-input"
                  />
                  <button
                    onClick={saveBusinessName}
                    disabled={brandingSaving}
                    className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium text-sm transition disabled:opacity-50 flex items-center gap-2"
                    data-testid="save-business-name-btn"
                  >
                    {brandingSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    {brandingSaved ? 'Saved' : brandingSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {/* PDF Preview hint */}
              <div className="bg-bg-light p-6 rounded-xl border border-primary/20">
                <h3 className="text-base font-semibold text-text-primary mb-2">How it works</h3>
                <ul className="text-sm text-text-secondary space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">1.</span>
                    Upload your logo and enter your business name above.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">2.</span>
                    Generate supports for any STL model in the "Generate Supports" workflow.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">3.</span>
                    Click "Export Quote PDF" — your logo and business name will appear in the document header and footer automatically.
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <h2 className="text-xl font-bold text-text-primary flex items-center gap-2 mb-6">
              <Lock className="w-5 h-5 text-primary" />
              Change Password
            </h2>

            {pwMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm flex items-center gap-2" data-testid="pw-success">
                <Check className="w-4 h-4" /> {pwMessage}
              </div>
            )}
            {pwError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="pw-error">
                {pwError}
              </div>
            )}

            <div className="space-y-4 max-w-md">
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                onClick={async () => {
                  setPwMessage('');
                  setPwError('');
                  if (!currentPassword || !newPassword) {
                    setPwError('Please fill in all fields.');
                    return;
                  }
                  if (newPassword.length < 8) {
                    setPwError('New password must be at least 8 characters.');
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    setPwError('New passwords do not match.');
                    return;
                  }
                  setPwLoading(true);
                  try {
                    await api.put('/auth/change-password', {
                      current_password: currentPassword,
                      new_password: newPassword,
                    });
                    setPwMessage('Password changed successfully.');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  } catch (err) {
                    setPwError(err.response?.data?.detail || 'Failed to change password.');
                  } finally {
                    setPwLoading(false);
                  }
                }}
                disabled={pwLoading}
                className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition disabled:opacity-50 flex items-center gap-2"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4" />
                {pwLoading ? 'Updating...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filament Profiles Tab */}
      {activeTab === 'filament' && (
        <div className="space-y-6">
          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                  <Beaker className="w-5 h-5 text-primary" />
                  Filament Price Profiles
                </h2>
                <p className="text-sm text-text-secondary mt-1">
                  Set your filament costs per kg for accurate ROI and quote calculations
                </p>
              </div>
              {isCustomPrices && (
                <span className="text-xs bg-green-500/15 text-green-400 px-2.5 py-1 rounded-full font-medium">Custom Prices Active</span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(filamentPrices).map(([material, price]) => (
                <div key={material} className="bg-bg-dark/50 p-4 rounded-lg border border-text-muted/10">
                  <label className="text-sm font-semibold text-text-primary block mb-1">{material}</label>
                  <p className="text-xs text-text-muted mb-2">
                    {material === 'PLA' && 'Most common, biodegradable'}
                    {material === 'ABS' && 'Durable, heat resistant'}
                    {material === 'PETG' && 'Strong, chemical resistant'}
                    {material === 'TPU' && 'Flexible, rubber-like'}
                    {material === 'PVA' && 'Water-soluble support'}
                    {material === 'HIPS' && 'Dissolvable support for ABS'}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted text-sm">$</span>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setFilamentPrices(prev => ({ ...prev, [material]: parseFloat(e.target.value) || 0 }))}
                      step="1"
                      min="0"
                      className="flex-1 bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary/50"
                      data-testid={`filament-price-${material.toLowerCase()}`}
                    />
                    <span className="text-text-muted text-xs">/kg</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-text-muted/10">
              <button
                onClick={saveFilamentPrices}
                disabled={filamentSaving}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition disabled:opacity-50"
                data-testid="save-filament-prices-btn"
              >
                {filamentSaved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {filamentSaved ? 'Saved!' : filamentSaving ? 'Saving...' : 'Save Prices'}
              </button>
              <button
                onClick={resetFilamentPrices}
                className="flex items-center gap-2 px-4 py-2.5 bg-bg-dark border border-text-muted/20 text-text-secondary hover:text-text-primary rounded-lg transition text-sm"
                data-testid="reset-filament-prices-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Defaults
              </button>
            </div>
          </div>

          <div className="bg-bg-light p-5 rounded-xl border border-accent/20">
            <p className="text-sm text-text-secondary">
              <span className="text-accent font-medium">How it works:</span> Your filament prices are used to calculate the ROI (Return on Investment) after each support generation, showing exactly how much money you save with tree supports vs standard block supports. They're also used in the Business Quote PDF generator.
            </p>
          </div>
        </div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-primary/20 to-accent/20 p-8 rounded-xl border border-primary/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Current Plan</h2>
                <p className="text-3xl font-bold text-primary capitalize">
                  {user?.subscription_plan || 'Free'}
                </p>
              </div>
              <Zap className="w-16 h-16 text-accent/50" />
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">Plan Features:</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {planFeatures[user?.subscription_plan || 'free'].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 text-text-secondary">
                    <span className="text-accent">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {user?.subscription_plan === 'free' ? (
              <button
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading}
                className="w-full py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                {upgrading ? 'Processing...' : 'Upgrade to Pro'}
              </button>
            ) : (
              <button
                onClick={handleManageSubscription}
                disabled={upgrading}
                className="w-full py-3 bg-bg-dark hover:bg-[#1f2332] text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                {upgrading ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
          </div>

          {/* Other Plans */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4">Available Plans</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
                <h3 className="text-lg font-bold text-white mb-2">Free</h3>
                <p className="text-3xl font-bold text-white mb-4">$0<span className="text-sm text-text-secondary">/mo</span></p>
                <ul className="space-y-2 text-sm text-text-secondary mb-4">
                  {planFeatures.free.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                {user?.subscription_plan === 'free' && (
                  <span className="block text-center py-2 bg-bg-dark text-text-secondary rounded-lg">
                    Current Plan
                  </span>
                )}
              </div>

              <div className="bg-gradient-to-br from-primary/20 to-accent/20 p-6 rounded-xl border-2 border-primary">
                <div className="text-xs font-semibold text-primary mb-2">MOST POPULAR</div>
                <h3 className="text-lg font-bold text-white mb-2">Pro</h3>
                <p className="text-3xl font-bold text-white mb-4">$14<span className="text-sm text-text-secondary">/mo</span></p>
                <ul className="space-y-2 text-sm text-text-secondary mb-4">
                  {planFeatures.pro.slice(0, 4).map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                {user?.subscription_plan === 'pro' ? (
                  <span className="block text-center py-2 bg-primary text-white rounded-lg">
                    Current Plan
                  </span>
                ) : (
                  <button
                    onClick={() => handleUpgrade('pro')}
                    disabled={upgrading}
                    className="w-full py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
                  >
                    {upgrading ? 'Processing...' : 'Upgrade'}
                  </button>
                )}
              </div>

              <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
                <h3 className="text-lg font-bold text-white mb-2">Studio</h3>
                <p className="text-3xl font-bold text-white mb-4">$29<span className="text-sm text-text-secondary">/mo</span></p>
                <ul className="space-y-2 text-sm text-text-secondary mb-4">
                  {planFeatures.studio.slice(0, 4).map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                {user?.subscription_plan === 'studio' ? (
                  <span className="block text-center py-2 bg-accent text-white rounded-lg">
                    Current Plan
                  </span>
                ) : (
                  <button
                    onClick={() => handleUpgrade('studio')}
                    disabled={upgrading}
                    className="w-full py-2 bg-accent hover:bg-accent-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
                  >
                    {upgrading ? 'Processing...' : 'Upgrade'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats Tab */}
      {activeTab === 'usage' && (
        <div className="space-y-6">
          <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center space-x-2">
              <Activity className="w-6 h-6 text-accent" />
              <span>Current Month Usage</span>
            </h2>

            <div className="space-y-6">
              {/* STL Analyses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white">STL Analyses</h3>
                    <p className="text-sm text-text-secondary">
                      {usage?.stl_analyses_used || 0} of {usage?.stl_analyses_limit || 0} used
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-primary">
                    {Math.round(
                      getUsagePercentage(usage?.stl_analyses_used, usage?.stl_analyses_limit)
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-bg-dark rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all"
                    style={{
                      width: `${getUsagePercentage(
                        usage?.stl_analyses_used,
                        usage?.stl_analyses_limit
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Image Analyses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Image Analyses</h3>
                    <p className="text-sm text-text-secondary">
                      {usage?.image_analyses_used || 0} of {usage?.image_analyses_limit || 0} used
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-accent">
                    {Math.round(
                      getUsagePercentage(usage?.image_analyses_used, usage?.image_analyses_limit)
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-bg-dark rounded-full h-3">
                  <div
                    className="bg-accent h-3 rounded-full transition-all"
                    style={{
                      width: `${getUsagePercentage(
                        usage?.image_analyses_used,
                        usage?.image_analyses_limit
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Chat Messages */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white">AI Chat Messages</h3>
                    <p className="text-sm text-text-secondary">
                      {usage?.chat_messages_used || 0} of {usage?.chat_messages_limit || 0} used
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-primary">
                    {Math.round(
                      getUsagePercentage(usage?.chat_messages_used, usage?.chat_messages_limit)
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-bg-dark rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all"
                    style={{
                      width: `${getUsagePercentage(
                        usage?.chat_messages_used,
                        usage?.chat_messages_limit
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {(usage?.stl_analyses_used >= usage?.stl_analyses_limit ||
              usage?.image_analyses_used >= usage?.image_analyses_limit ||
              usage?.chat_messages_used >= usage?.chat_messages_limit) && (
              <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <p className="text-warning text-sm font-medium">
                  You've reached one or more of your monthly limits. Consider upgrading your plan
                  or redeeming points for extra quota!
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
